import React from 'react';

import find from 'lodash/find';
import {useSelector} from 'react-redux';
import {Link} from 'react-router';

import GenericAttachedItemsSidebar from 'app/components/general/GenericAttachedItemsSidebar';
import {
  GenericAttachedItemsSidebarItem,
  GenericSidebarItemStructureActionButton,
} from 'app/components/general/GenericAttachedItemsSidebar/GenericAttachedItemsSidebarItem';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {CurrentUserSelector} from 'app/current-user/api';
import {
  approveProcessVersion,
  fetchData as fetchProcesses,
  fetchProcessLatestVersion,
  searchProcesses,
  updateProcessVersion,
} from 'app/process/actions';
import {useModelChangeSubscription} from 'app/subscriptions';
import {PROCESS_CREATE_PAGE_URL} from 'app/urls';
import {SIDEBAR_PAGE_SIZE, UNLIMITED_PAGE_SIZE} from 'app/utils/constants';
import {reportUnexpectedState} from 'app/utils/errors';
import {getProcessName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';
import {objectsToFlatArray, useAsyncActions} from 'app/utils/redux';
import {get} from 'app/utils/request';

import {ProcedureSerializedType, ProcessSerializedType} from 'sweet-types';

import type {
  GenericActionableType,
  Props as SidebarItemProps,
} from 'app/components/general/GenericAttachedItemsSidebar/GenericAttachedItemsSidebarItem';
import type {StoredResponseType} from 'app/utils/redux_v2';
type Props = {
  onClose: () => void;
  procedure: ProcedureSerializedType;
  canEditProcedure: boolean;
  fetchProcedure: () => Promise<any>;
};

export const ProcedureProcessesSidebar = ({
  canEditProcedure,
  procedure,
  fetchProcedure,
  onClose,
}: Props) => {
  const user = useSelector((state) => CurrentUserSelector(state).data!);
  const items = useSelector((state) => {
    const processes = procedure.url && state.processArea.get('processes');

    return (
      typeof processes !== 'undefined' ? objectsToFlatArray(processes) : []
    ).filter(
      (process: ProcessSerializedType) =>
        process.content.filter(
          (m) =>
            m.type == 'processprocedure' &&
            !m.deleted &&
            m.url === procedure.url
        ).length
    );
  });

  const actions = useAsyncActions({
    fetchProcesses,
    fetchProcessLatestVersion,
    approveProcessVersion,
    updateProcessVersion,
  });

  const loadItems = () =>
    Promise.all([
      actions.fetchProcesses({
        page: '1',
        page_size: UNLIMITED_PAGE_SIZE,
        ordering: 'name',
      }),
      fetchProcedure(),
    ]);

  const getItemComponentProps = (
    process: ProcessSerializedType,
    attached: boolean
  ) => {
    const hasDrafts = process.recent_draft.id !== process.current_version.id;
    return {
      hasDrafts,
      disableAndShowTooltipText: hasDrafts
        ? gt('This process has unapproved versions, click to review')
        : !canEditProcedure
        ? gt('Only procedure managers can add to or remove from process')
        : !process.can_edit
        ? gt('Only this process managers can add or remove procedures')
        : null,
      actionTooltip: attached
        ? gt('Remove procedure from this process')
        : gt('Add procedure to this process'),
    };
  };

  function getItemComponent<T extends GenericActionableType>({
    hasDrafts,
    ...props
  }: SidebarItemProps<T> & {hasDrafts: boolean}) {
    return (
      <GenericAttachedItemsSidebarItem
        {...props}
        getActionButtonComponent={(actionButtonProps) =>
          hasDrafts ? (
            <Link
              className="btn btn btn-link p-2"
              to={`${props.item.html_url}edit/`}
            >
              <i className="fa fa-fw fa-pen" />
            </Link>
          ) : (
            <GenericSidebarItemStructureActionButton {...actionButtonProps} />
          )
        }
      />
    );
  }

  useModelChangeSubscription(
    (message, {runWhenAllRelatedMatch}) => {
      runWhenAllRelatedMatch(
        message,
        'process',
        {
          content_type: 'procedure',
          id: parseInt(String(procedure.id)),
        },
        () => loadItems()
      );
    },
    [procedure, loadItems]
  );

  const onRemove = (process: ProcessSerializedType) =>
    actions.fetchProcessLatestVersion(process.id).then((version) => {
      const membership = find(version.content, {
        url: procedure.url,
      });

      if (membership != null) {
        return actions
          .updateProcessVersion(version.url, {
            content: [
              {
                id: procedure.hashid,
                procedure_id: procedure.id,
                title: procedure.name,
                type: 'processprocedure',
                n: version.content.length + 1,
                delete: true,
              },
            ],
          })
          .then((processVersion) =>
            actions.approveProcessVersion(
              processVersion.url,
              process.id,
              {notify: false, signoff: false},
              'A new version without this procedure was created and approved'
            )
          );
      }

      return Promise.resolve(
        reportUnexpectedState('USEC12', {
          procedureURL: procedure.url,
          process,
        })
      );
    });

  const filterProcesses = (
    response: StoredResponseType<ProcessSerializedType>
  ) => ({
    ...response,
    results: response.results.filter((p) => p.can_edit),
  });
  const onSearch = (search) =>
    searchProcesses(search, SIDEBAR_PAGE_SIZE).then(filterProcesses);
  const onLoadMore = (response) => get(response.next).then(filterProcesses);
  const onAttach = (process: ProcessSerializedType) =>
    actions.fetchProcessLatestVersion(process.id).then((version) =>
      actions
        .updateProcessVersion(version.url, {
          content: [
            {
              id: procedure.hashid,
              procedure_id: procedure.id,
              title: procedure.name,
              type: 'processprocedure',
              n: version.content.length + 1,
            },
          ],
        })
        .then((processVersion) =>
          actions.approveProcessVersion(
            processVersion.url,
            process.id,
            {notify: false, signoff: false},
            'A new version with this procedure was created and approved'
          )
        )
    );

  return (
    <GenericAttachedItemsSidebar
      loadItems={loadItems}
      title={'Processes'}
      itemNameProcessor={getProcessName}
      itemComponentProps={getItemComponentProps}
      ItemComponent={getItemComponent}
      onRemove={onRemove}
      showFilterField={user.is_manager && canEditProcedure}
      searchPlaceholder={gt('Search processes...')}
      onSearch={onSearch}
      onLoadMore={onLoadMore}
      onAttach={onAttach}
      canCreate={
        user.team_memberships.filter(
          (membership) => membership.access != 'reader'
        ).length > 0 ||
        user.is_manager ||
        user.is_super_teammate
      }
      createLabel={gt('Create Process')}
      createURL={PROCESS_CREATE_PAGE_URL}
      emptyResultsComponents={
        <NoResultsMessage
          onSidebar={true}
          icon="fa-copy"
          head={gt('No processes')}
        >
          {user.is_manager && canEditProcedure
            ? gt(
                'You can add this procedure to existing processes, remove it from a process or create a new process using the button below'
              )
            : null}
        </NoResultsMessage>
      }
      onClose={onClose}
      items={items}
    />
  );
};
